import { Button, Chip, Select, SelectItem, Spinner } from '@heroui/react';
import { Languages, Wand2 } from 'lucide-react';
import React, { FC, useState } from 'react';

import { useHeroToast } from '../../../../../hooks';
import { AD_COPY_LANGUAGES, AdCopyMode, useGenerateAdCopy } from '../../../../../queries';
import { useAdsGeneratorStore } from '../../../model/store';
import { TextElement } from '../../../model/types';
import { ComposerHint, FieldGroup, FieldTitle } from './AiComposer.styled';

/**
 * Praca nad samymi napisami: warianty i tłumaczenie.
 *
 * Świadomie NIE rusza układu — model dostaje tylko teksty, a podmiana idzie
 * element po elemencie. Dzięki temu można poprawić treść, nie tracąc kreacji,
 * którą ktoś przed chwilą ułożył.
 */
export const CopyAssistant: FC = () => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const commit = useAdsGeneratorStore((state) => state.commit);
  const updateElement = useAdsGeneratorStore((state) => state.updateElement);
  const { mutateAsync: generate, isPending } = useGenerateAdCopy();
  const { showError, showSuccess } = useHeroToast();

  const [language, setLanguage] = useState('en');
  const [options, setOptions] = useState<Record<string, string[]>>({});

  if (!scene) return null;

  const texts = scene.elements.filter(
    (element): element is TextElement => element.type === 'text' && element.text.trim().length > 0,
  );

  const run = async (mode: AdCopyMode): Promise<void> => {
    try {
      const result = await generate({
        mode,
        language: mode === 'translate' ? language : undefined,
        items: texts.map((element) => ({ id: element.id, text: element.text })),
      });

      const next: Record<string, string[]> = {};
      result.items.forEach((item) => {
        next[item.id] = item.options;
      });
      setOptions(next);

      if (mode === 'translate') {
        // Tłumaczenie ma jedną poprawną odpowiedź, więc podstawiamy je od razu.
        commit();
        result.items.forEach((item) => {
          if (item.options[0]) updateElement(item.id, { text: item.options[0] }, true);
        });
        showSuccess('Napisy przetłumaczone. Sprawdź, czy mieszczą się w ramkach.');
      }
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się przygotować tekstów');
    }
  };

  const apply = (elementId: string, text: string): void => {
    commit();
    updateElement(elementId, { text }, true);
  };

  if (texts.length === 0) {
    return (
      <FieldGroup>
        <FieldTitle>Teksty na planszy</FieldTitle>
        <ComposerHint>Na planszy nie ma jeszcze żadnego napisu.</ComposerHint>
      </FieldGroup>
    );
  }

  return (
    <FieldGroup>
      <FieldTitle>{`Teksty na planszy (${texts.length})`}</FieldTitle>

      <div className="flex flex-wrap items-end gap-2">
        <Button
          size="sm"
          radius="sm"
          variant="flat"
          startContent={<Wand2 size={16} />}
          isDisabled={isPending}
          onPress={() => run('variants')}
        >
          Zaproponuj warianty
        </Button>

        <Select
          size="sm"
          radius="sm"
          className="max-w-40"
          aria-label="Język tłumaczenia"
          selectedKeys={new Set([language])}
          onSelectionChange={(keys) => {
            const [next] = Array.from(keys as Set<string>);
            if (next) setLanguage(next);
          }}
        >
          {AD_COPY_LANGUAGES.map((item) => (
            <SelectItem key={item.code} textValue={item.label}>
              {item.label}
            </SelectItem>
          ))}
        </Select>

        <Button
          size="sm"
          radius="sm"
          variant="flat"
          startContent={<Languages size={16} />}
          isDisabled={isPending}
          onPress={() => run('translate')}
        >
          Przetłumacz
        </Button>

        {isPending && <Spinner size="sm" />}
      </div>

      <div className="flex flex-col gap-2">
        {texts.map((element) => (
          <div key={element.id}>
            <p className="text-xs text-gray-800">{element.text}</p>
            {(options[element.id]?.length ?? 0) > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {options[element.id].map((option) => (
                  <Chip
                    key={option}
                    size="sm"
                    radius="sm"
                    variant="flat"
                    className="cursor-pointer text-xs"
                    onClick={() => apply(element.id, option)}
                  >
                    {option}
                  </Chip>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <ComposerHint>
        Kliknięcie propozycji podmienia treść napisu. Układ i style zostają bez zmian.
      </ComposerHint>
    </FieldGroup>
  );
};
